import { createUuid } from '@notifi/uuid/browser';
/**
 * W3C Trace Context + request-id helpers for outgoing API calls.
 *
 * Deliberately dependency-free: no OpenTelemetry SDK ships in the bundle. The
 * backend services run the OTel W3C propagator (docs/observability/DESIGN.md
 * §4), so a well-formed `traceparent` is all that is needed for the server span
 * to join the trace this header names.
 *
 * Header ownership (see `lib/monitoring.ts`):
 *   - `x-request-id` is ALWAYS minted here, one UUID per request. The backend
 *     honours it, logs it, and echoes it; it is what an error toast shows.
 *   - `traceparent` is minted here only when CloudWatch RUM is not injecting
 *     its own. The two can never both set it — XHR combines duplicate header
 *     values into one comma-joined string that no propagator accepts.
 */

export const REQUEST_ID_HEADER = 'x-request-id';
export const TRACEPARENT_HEADER = 'traceparent';

// ─── traceparent ownership ────────────────────────────────────────────────────

let _traceparentOwnedExternally = false;

/**
 * Hand `traceparent` to another writer (CloudWatch RUM's HTTP plugin) or take
 * it back. Set by `lib/monitoring.ts` once RUM is up; read by the axios
 * clients, which mint their own header only while this is false. Lives here,
 * not in monitoring, so the API layer never depends on the monitoring module
 * (which tests routinely mock).
 */
export function setTraceparentOwnedExternally(owned: boolean): void {
  _traceparentOwnedExternally = owned;
}

export function isTraceparentOwnedExternally(): boolean {
  return _traceparentOwnedExternally;
}

/** `00-<32 hex trace id>-<16 hex span id>-<2 hex flags>` */
const TRACEPARENT_PATTERN = /^00-([0-9a-f]{32})-([0-9a-f]{16})-([0-9a-f]{2})$/;

export interface TraceContext {
  /** 32 lowercase hex chars, never all zeros. */
  traceId: string;
  /** 16 lowercase hex chars, never all zeros. */
  spanId: string;
  /** The `traceparent` header value, unsampled for log correlation without trace export. */
  traceparent: string;
}

function randomHex(bytes: number): string {
  const buffer = new Uint8Array(bytes);
  crypto.getRandomValues(buffer);
  let hex = '';
  for (const byte of buffer) hex += byte.toString(16).padStart(2, '0');
  return hex;
}

/** Trace Context forbids the all-zero id; re-roll on the astronomically rare hit. */
function randomNonZeroHex(bytes: number): string {
  let hex = randomHex(bytes);
  while (/^0+$/.test(hex)) hex = randomHex(bytes);
  return hex;
}

/** A fresh UUID v4 request id. */
export function createRequestId(): string {
  return createUuid();
}

/**
 * A new root trace context. Every request gets its own trace: the SPA has no
 * long-lived parent span, so a request IS a trace from the browser's point of
 * view, and the server span becomes its first child.
 */
export function createTraceContext(): TraceContext {
  const traceId = randomNonZeroHex(16);
  const spanId = randomNonZeroHex(8);
  return { traceId, spanId, traceparent: `00-${traceId}-${spanId}-00` };
}

/** Parse a `traceparent` value; null when it is not a valid version-00 header. */
export function parseTraceparent(
  value: string | null | undefined,
): { traceId: string; spanId: string; sampled: boolean } | null {
  if (!value) return null;
  const match = TRACEPARENT_PATTERN.exec(value.trim());
  if (!match) return null;
  const [, traceId, spanId, flags] = match;
  if (/^0+$/.test(traceId!) || /^0+$/.test(spanId!)) return null;
  return { traceId: traceId!, spanId: spanId!, sampled: (parseInt(flags!, 16) & 0x01) === 1 };
}
